import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalMedia,
} from '@unmind/design-system';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OnboardingSupportOption } from '__generated__/globalTypes';
import {
  DetailView,
  DetailViewCta,
  DETAIL_VIEW_HEADING_ID,
} from './DetailView';
import { OnboardingSupportOptions_onboardingSupportOptions_practicalSupport as PracticalSupportDetails } from './graphql/__generated__/OnboardingSupportOptions';
import {
  QuestionView,
  QUESTION_VIEW_HEADING_ID,
  QUESTION_VIEW_IMAGE,
} from './QuestionView';
import { getSupportOptionConfig } from './supportOptions';
import { getCtaDestination } from './ctaDestinations';
import { useSupportOptionCta } from './useSupportOptionCta';
import {
  DismissMethod,
  useWelcomeModalTracking,
  WelcomeModalEntryPoint,
} from './useWelcomeModalTracking';
import './WelcomeModal.scss';

export interface WelcomeModalProps {
  open: boolean;
  options: OnboardingSupportOption[];
  firstName?: string | null;
  practicalSupport?: PracticalSupportDetails | null;
  entryPoint: WelcomeModalEntryPoint;
  onComplete(): void;
  onDismiss(method: DismissMethod): void;
}

/**
 * The onboarding welcome modal: the question, and a detail view per offering.
 *
 * Composes the design system's regions here because the modal matches them by
 * element type — one returned from inside a view is not recognised.
 */
export const WelcomeModal = ({
  open,
  options,
  firstName,
  practicalSupport,
  entryPoint,
  onComplete,
  onDismiss,
}: WelcomeModalProps) => {
  const { t: translate } = useTranslation('homepage', {
    keyPrefix: 'welcome_modal',
  });
  const [picked, setPicked] = useState<OnboardingSupportOption>();

  const {
    trackOptionClicked,
    trackCtaClicked,
    trackDismissed,
    trackBackClicked,
  } = useWelcomeModalTracking({ shown: open, options, entryPoint });

  const onCtaClick = useSupportOptionCta({
    practicalSupport,
    onNavigate: option => {
      trackCtaClicked(
        option,
        getCtaDestination(option, practicalSupport) ?? '',
      );
      onComplete();
    },
  });

  const dismiss = (method: DismissMethod) => {
    trackDismissed(method);
    onDismiss(method);
  };

  const selectOption = (option: OnboardingSupportOption) => {
    trackOptionClicked(option);
    setPicked(option);
  };

  const goBack = () => {
    if (picked) {
      trackBackClicked(picked);
    }

    setPicked(undefined);
  };

  const image = picked
    ? getSupportOptionConfig(picked)?.detailImage
    : QUESTION_VIEW_IMAGE;

  return (
    <Modal
      open={open}
      onClose={() => dismiss('close')}
      closeLabel={translate('close')}
      labelledBy={picked ? DETAIL_VIEW_HEADING_ID : QUESTION_VIEW_HEADING_ID}
    >
      <ModalMedia>
        {image !== undefined && (
          <img className="welcome-modal__image" src={image} alt="" />
        )}
      </ModalMedia>
      <ModalContent>
        {picked ? (
          <DetailView option={picked} onBack={goBack} />
        ) : (
          <QuestionView
            options={options}
            firstName={firstName}
            onSelectOption={selectOption}
            onNotSure={() => dismiss('not-sure')}
          />
        )}
      </ModalContent>
      {picked && (
        <ModalFooter>
          <DetailViewCta option={picked} onCtaClick={onCtaClick} />
        </ModalFooter>
      )}
    </Modal>
  );
};
